// Personalakte im Widget-Stil (design_handoff_personal §5, Screens 13–15, 21, 27). Reine Darstellung —
// Laden, Speichern und Löschen bleiben in der Seite bzw. in lib/personal/akte.
import type { ReactNode } from 'react';
import {
  AirplaneTilt, CalendarCheck, Car, Check, FileArrowDown, FileText, FirstAid, GraduationCap, HardHat, HourglassMedium,
  IdentificationCard, ShieldCheck, Stethoscope, Trash, UploadSimple, Warning, X, type Icon,
} from '@phosphor-icons/react';
import {
  DOKUMENT_ART, NACHWEIS_ART, aktuelleNachweise, ampel,
  type DokumentArt, type Nachweis, type NachweisArt, type Personalakte, type PersonalDokument,
} from '../../../../lib/personal/akte';
import { ampelChip, ampelFarbe, initialen } from '../../../../lib/personalAnsicht/darstellung';

export const datum = (d: string | null | undefined) =>
  d ? new Date(`${d}T12:00:00`).toLocaleDateString('de-DE') : '—';

const NACHWEIS_ICON: Record<NachweisArt, Icon> = {
  fuehrerschein: Car, unterweisung: HardHat, schulung: GraduationCap, untersuchung: Stethoscope,
};

const FELD = {
  height: 44, padding: '0 12px', borderRadius: 13, border: 'none', outline: 'none', background: 'var(--bg)',
  boxShadow: 'inset 0 0 0 1px var(--line)', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: 'var(--ink)', minWidth: 0,
} as const;
const KLEINFELD = { ...FELD, height: 40, borderRadius: 12, fontSize: 13 } as const;
const LABEL = { fontSize: 12, fontWeight: 700, color: 'var(--ink3)' } as const;

function WidgetKopf({ icon: I, farbe, flaeche, children }: { icon: Icon; farbe: string; flaeche: string; children: ReactNode }) {
  return (
    <span className="flex items-center" style={{ gap: 8, fontSize: 15, fontWeight: 800 }}>
      <span className="flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 10, background: flaeche }}>
        <I size={15} weight="fill" color={farbe} />
      </span>
      {children}
    </span>
  );
}

function RundKnopf({ icon: I, label, tip, onClick, gefahr }: { icon: Icon; label: string; tip: string; onClick: () => void; gefahr?: boolean }) {
  return (
    <button type="button" aria-label={label} data-tip={tip} onClick={onClick} className="immo-tippziel flex shrink-0 items-center justify-center"
      style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer', background: gefahr ? 'var(--bad-soft)' : 'var(--fill2)', color: gefahr ? 'var(--bad)' : 'var(--ink)' }}>
      <I size={14} weight="bold" />
    </button>
  );
}

// ─── Warnung „Nachweise laufen ab" (13) ─────────────────────────────────────────

/** Klick auf einen Chip wählt die Person aus (Freigabe 18.10.2026: „Akte öffnen“ aus dem Prototyp). */
export function NachweisWarnung({ nachweise, name, onOeffnen }: { nachweise: Nachweis[]; name: (id: string) => string; onOeffnen: (mitarbeiterId: string) => void }) {
  return (
    <div className="flex flex-col" style={{ borderRadius: 22, padding: '14px 16px', gap: 8, background: 'var(--warn-soft)' }}>
      <span className="flex items-center" style={{ gap: 8, fontSize: 13.5, fontWeight: 800, color: 'var(--warn)' }}>
        <Warning size={17} weight="fill" />Nachweise laufen ab
      </span>
      <div className="flex flex-wrap" style={{ gap: 6 }}>
        {nachweise.map((n) => (
          <button key={n.id} type="button" onClick={() => onOeffnen(n.mitarbeiter_id)} data-tip="Akte öffnen – Zur Personalakte dieser Person"
            className="immo-tippziel immo-zeile-hover inline-flex max-w-full items-center text-left"
            style={{ minHeight: 30, padding: '4px 12px', borderRadius: 15, gap: 6, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 12, fontWeight: 700, background: 'var(--bg)', color: 'var(--ink)' }}>
            <span className="shrink-0" style={{ width: 7, height: 7, borderRadius: '50%', background: ampelFarbe(ampel(n.naechste_faellig)).farbe }} />
            {NACHWEIS_ART[n.art].label} · {name(n.mitarbeiter_id)} · {datum(n.naechste_faellig)}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Stammdaten (13–15) ─────────────────────────────────────────────────────────

type TextSchluessel = Exclude<keyof Personalakte, 'mitarbeiter_id'>;

const BEARBEIT_FELDER: readonly (readonly [TextSchluessel, string, 'text' | 'date'])[] = [
  ['personalnummer', 'Personalnummer', 'text'], ['position', 'Position', 'text'],
  ['abteilung', 'Abteilung', 'text'], ['eintritt', 'Eintritt', 'date'],
  ['befristet_bis', 'Befristet bis', 'date'], ['austritt', 'Austritt', 'date'],
  ['geburtsdatum', 'Geburtsdatum', 'date'], ['telefon_privat', 'Telefon privat', 'text'],
  ['adresse', 'Adresse', 'text'], ['notfall_name', 'Notfallkontakt', 'text'],
  ['notfall_telefon', 'Notfall-Telefon', 'text'], ['bemerkung', 'Bemerkung', 'text'],
];

export function StammdatenWidget({
  name, akte, jahre, verwalten, bearbeiten, entwurf, onEntwurf, lohnEntwurf, onLohnEntwurf, stundenlohnText, arbeitet, onSpeichern,
}: {
  name: string; akte: Personalakte | null; jahre: number | null; verwalten: boolean; bearbeiten: boolean;
  entwurf: Partial<Personalakte>; onEntwurf: (e: Partial<Personalakte>) => void;
  lohnEntwurf: string; onLohnEntwurf: (t: string) => void; stundenlohnText: string; arbeitet: boolean; onSpeichern: () => void;
}) {
  const rolle = [akte?.position, akte?.abteilung].filter(Boolean).join(' · ');
  const ansicht: [string, string | null | undefined][] = [
    ['Position', akte?.position], ['Abteilung', akte?.abteilung],
    ['Eintritt', datum(akte?.eintritt)], ['Befristet bis', datum(akte?.befristet_bis)],
    ['Geburtsdatum', datum(akte?.geburtsdatum)], ['Telefon privat', akte?.telefon_privat],
    ['Adresse', akte?.adresse],
    ['Notfallkontakt', [akte?.notfall_name, akte?.notfall_telefon].filter(Boolean).join(' · ')],
    // Nur Personal/GF bekommt die Zeile überhaupt zu sehen — der Wert kommt
    // aus einer Tabelle, die für alle anderen leer bleibt.
    ...(verwalten ? [['Stundenlohn', stundenlohnText] as [string, string]] : []),
  ];
  return (
    <div className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: 20, gap: 14 }}>
      <div className="flex items-center" style={{ gap: 14 }}>
        <span className="flex shrink-0 items-center justify-center"
          style={{ width: 64, height: 64, borderRadius: '50%', fontSize: 22, fontWeight: 800, background: 'var(--acc-fill)', boxShadow: 'var(--acc-sh)', color: 'var(--acc-ink)' }}>
          {initialen(name) || '?'}
        </span>
        <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 4 }}>
          <span className="truncate" style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>{name}</span>
          {rolle && <span style={{ fontSize: 13, color: 'var(--ink2)' }}>{rolle}</span>}
          <div className="flex flex-wrap" style={{ gap: 6 }}>
            {akte?.personalnummer && (
              <span data-tip="Personalnummer – Wird für den DATEV-Export verwendet" className="inline-flex items-center"
                style={{ height: 24, padding: '0 10px', borderRadius: 12, fontSize: 11.5, fontWeight: 800, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
                Nr. {akte.personalnummer}
              </span>
            )}
            {jahre !== null && (
              <span data-tip="Dienstjahre – Seit dem Eintritt" className="inline-flex items-center"
                style={{ height: 24, padding: '0 10px', borderRadius: 12, fontSize: 11.5, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink2)' }}>
                {jahre} Jahre dabei
              </span>
            )}
          </div>
        </div>
      </div>

      {bearbeiten && verwalten ? (
        <>
          <div className="grid" style={{ gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))' }}>
            {BEARBEIT_FELDER.map(([key, label, typ]) => (
              <label key={key} className="flex min-w-0 flex-col" style={{ gap: 5 }}>
                <span style={LABEL}>{label}</span>
                <input type={typ} value={entwurf[key] ?? ''} onChange={(e) => onEntwurf({ ...entwurf, [key]: e.target.value || null })} style={FELD} />
              </label>
            ))}
          </div>
          <label className="flex flex-col" style={{ gap: 5 }}>
            <span style={LABEL}>Stundenlohn (€) · nur Personal/GF</span>
            <input type="text" inputMode="decimal" value={lohnEntwurf} onChange={(e) => onLohnEntwurf(e.target.value)}
              placeholder="z. B. 14,00 — leer lassen, wenn Festgehalt" style={FELD} />
          </label>
          <button data-tip="Speichern – Stammdaten und Stundenlohn sichern" type="button" onClick={onSpeichern} disabled={arbeitet} className="inline-flex items-center self-end"
            style={{ height: 44, padding: '0 20px', gap: 7, borderRadius: 14, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 800,
              background: 'var(--acc-fill)', boxShadow: 'var(--acc-sh)', color: 'var(--acc-ink)', opacity: arbeitet ? 0.5 : 1 }}>
            <Check size={14} weight="bold" />{arbeitet ? 'Speichert …' : 'Speichern'}
          </button>
        </>
      ) : (
        <>
          <div className="flex flex-col">
            {ansicht.map(([label, wert]) => (
              <div key={label} className="flex justify-between" style={{ gap: 14, padding: '10px 0', borderBottom: '1px solid var(--line)', fontSize: 13.5 }}>
                <span className="whitespace-nowrap" style={{ color: 'var(--ink2)' }}>{label}</span>
                <b className="min-w-0 text-right" style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{wert || '—'}</b>
              </div>
            ))}
          </div>
          {akte?.bemerkung && (
            <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)', padding: '10px 12px', borderRadius: 14, background: 'var(--fill)' }}>{akte.bemerkung}</span>
          )}
        </>
      )}
    </div>
  );
}

// ─── Urlaub und Krankheit (14) ──────────────────────────────────────────────────

export function UrlaubKacheln({ konto, kranktage }: { konto: { anspruch: number; genommen: number; rest: number }; kranktage: number }) {
  const de = (n: number) => n.toLocaleString('de-DE');
  const kacheln: { l: string; v: string; vc: string; icon: Icon; c: string; bg: string; tip: string }[] = [
    { l: 'Urlaubsanspruch', v: `${de(konto.anspruch)} Tage`, vc: 'var(--ink)', icon: CalendarCheck, c: 'var(--acc)', bg: 'var(--acc-soft)', tip: 'Urlaubsanspruch – Tage im laufenden Jahr' },
    { l: 'Genommen', v: de(konto.genommen), vc: 'var(--ink)', icon: AirplaneTilt, c: 'var(--ok)', bg: 'var(--ok-soft)', tip: 'Genommen – Genehmigte Urlaubstage' },
    { l: 'Resturlaub', v: de(konto.rest), vc: konto.rest >= 0 ? 'var(--ok)' : 'var(--bad)', icon: HourglassMedium, c: 'var(--info)', bg: 'var(--info-soft)', tip: 'Resturlaub – Noch verfügbare Tage' },
    { l: 'Kranktage dieses Jahr', v: String(kranktage), vc: 'var(--ink)', icon: FirstAid, c: 'var(--bad)', bg: 'var(--bad-soft)', tip: 'Kranktage – Genehmigte Krankmeldungen in diesem Jahr' },
  ];
  return (
    <div className="grid" style={{ gap: 12, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
      {kacheln.map((k) => {
        const I = k.icon;
        return (
          <div key={k.l} data-tip={k.tip} className="immo-glas flex flex-col" style={{ borderRadius: 24, padding: 16, gap: 8 }}>
            <span className="flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: k.bg }}>
              <I size={17} weight="fill" color={k.c} />
            </span>
            <span style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', color: k.vc }}>{k.v}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{k.l}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Nachweise und Kontrollen ───────────────────────────────────────────────────

export interface NachweisEntwurf { art: NachweisArt; am: string; faellig: string; bemerkung: string }

export function NachweiseWidget({ nachweise, verwalten, entwurf, onEntwurf, onArtWechsel, onAmWechsel, onEintragen, onSchliessen, onLoeschen, arbeitet }: {
  nachweise: Nachweis[]; verwalten: boolean; entwurf: NachweisEntwurf | null; onEntwurf: (e: NachweisEntwurf) => void;
  onArtWechsel: (art: NachweisArt) => void; onAmWechsel: (am: string) => void;
  onEintragen: () => void; onSchliessen: () => void; onLoeschen: (n: Nachweis) => void; arbeitet: boolean;
}) {
  return (
    <div className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: 18, gap: 12 }}>
      <WidgetKopf icon={ShieldCheck} farbe="var(--ok)" flaeche="var(--ok-soft)">Nachweise und Kontrollen</WidgetKopf>
      {entwurf && verwalten && (
        <div className="immo-pop-in flex flex-col" style={{ borderRadius: 18, padding: 12, gap: 10, background: 'var(--fill)' }}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onSchliessen(); } }}>
          <div role="radiogroup" aria-label="Art des Nachweises" className="grid" style={{ gap: 6, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            {(Object.keys(NACHWEIS_ART) as NachweisArt[]).map((k) => {
              const an = entwurf.art === k;
              const I = NACHWEIS_ICON[k];
              return (
                <button data-tip={`${NACHWEIS_ART[k].label} – alle ${NACHWEIS_ART[k].intervallMonate} Monate fällig`} key={k} type="button" role="radio" aria-checked={an} onClick={() => onArtWechsel(k)} className="flex items-center text-left"
                  style={{ minHeight: 40, padding: '6px 10px', gap: 7, borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
                    background: an ? 'var(--acc-soft)' : 'var(--bg)', color: an ? 'var(--acc)' : 'var(--ink2)', boxShadow: an ? 'inset 0 0 0 2px var(--acc)' : 'inset 0 0 0 1px var(--line)' }}>
                  <I size={15} weight="fill" className="shrink-0" />{NACHWEIS_ART[k].label}
                </button>
              );
            })}
          </div>
          <div className="grid" style={{ gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
            <label className="flex flex-col" style={{ gap: 4 }}>
              <span style={{ ...LABEL, fontSize: 11.5 }}>durchgeführt</span>
              <input type="date" value={entwurf.am} onChange={(e) => onAmWechsel(e.target.value)} style={KLEINFELD} />
            </label>
            <label className="flex flex-col" style={{ gap: 4 }}>
              <span style={{ ...LABEL, fontSize: 11.5 }}>nächste Fälligkeit</span>
              <input type="date" value={entwurf.faellig} onChange={(e) => onEntwurf({ ...entwurf, faellig: e.target.value })} style={KLEINFELD} />
            </label>
          </div>
          <div className="flex" style={{ gap: 6 }}>
            <input value={entwurf.bemerkung} onChange={(e) => onEntwurf({ ...entwurf, bemerkung: e.target.value })} placeholder="Bemerkung" className="flex-1" style={KLEINFELD} />
            <button data-tip="Eintragen – Nachweis speichern" className="immo-tippziel" type="button" onClick={onEintragen} disabled={arbeitet}
              style={{ height: 40, padding: '0 14px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 800,
                background: 'var(--acc-fill)', color: 'var(--acc-ink)', opacity: arbeitet ? 0.5 : 1 }}>Eintragen</button>
            <button data-tip="Abbrechen – Formular schließen" type="button" aria-label="Abbrechen" onClick={onSchliessen} className="immo-tippziel flex items-center justify-center"
              style={{ width: 40, height: 40, borderRadius: 12, border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink)' }}>
              <X size={13} weight="bold" />
            </button>
          </div>
        </div>
      )}
      {nachweise.length === 0 ? (
        <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine Nachweise erfasst</span>
      ) : aktuelleNachweise(nachweise).map((n) => {
        const chip = ampelChip(ampel(n.naechste_faellig), datum(n.naechste_faellig));
        return (
          <div key={n.id} className="flex flex-wrap items-center" style={{ gap: 12, padding: '10px 12px', borderRadius: 16, background: 'var(--fill)' }}>
            <span className="flex shrink-0 items-center justify-center" style={{ width: 36, height: 36, borderRadius: 12, background: chip.flaeche }}>
              <IdentificationCard size={17} weight="fill" color={chip.farbe} />
            </span>
            <div className="flex min-w-0 flex-col" style={{ flex: '1 1 160px', gap: 1 }}>
              <span style={{ fontSize: 14, fontWeight: 800 }}>{NACHWEIS_ART[n.art].label}</span>
              <span style={{ fontSize: 12, color: 'var(--ink3)' }}>zuletzt {datum(n.durchgefuehrt_am)}{n.bemerkung ? ` · ${n.bemerkung}` : ''}</span>
            </div>
            <span className="inline-flex items-center whitespace-nowrap"
              style={{ height: 26, padding: '0 10px', borderRadius: 13, fontSize: 11.5, fontWeight: 800, background: chip.flaeche, color: chip.farbe }}>
              {chip.text}
            </span>
            {verwalten && <RundKnopf icon={Trash} label="Löschen" tip="Löschen – Nachweis entfernen" gefahr onClick={() => onLoeschen(n)} />}
          </div>
        );
      })}
    </div>
  );
}

// ─── Dokumente ──────────────────────────────────────────────────────────────────

export interface DokumentEntwurf { art: DokumentArt; titel: string; datei: File | null; gueltigBis: string }

const endung = (name: string | null) => (name?.split('.').pop() || 'DOC').slice(0, 4).toUpperCase();

export function DokumenteWidget({ dokumente, verwalten, entwurf, onEntwurf, onAblegen, onSchliessen, onOeffnen, onLoeschen, arbeitet }: {
  dokumente: PersonalDokument[]; verwalten: boolean; entwurf: DokumentEntwurf | null; onEntwurf: (e: DokumentEntwurf) => void;
  onAblegen: () => void; onSchliessen: () => void; onOeffnen: (d: PersonalDokument) => void; onLoeschen: (d: PersonalDokument) => void; arbeitet: boolean;
}) {
  return (
    <div className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: 18, gap: 12 }}>
      <WidgetKopf icon={FileText} farbe="var(--info)" flaeche="var(--info-soft)">Dokumente</WidgetKopf>
      {entwurf && verwalten && (
        <div className="immo-pop-in flex flex-col" style={{ borderRadius: 18, padding: 12, gap: 10, background: 'var(--fill)' }}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onSchliessen(); } }}>
          <div role="radiogroup" aria-label="Art des Dokuments" className="flex flex-wrap" style={{ gap: 6 }}>
            {(Object.keys(DOKUMENT_ART) as DokumentArt[]).map((k) => {
              const an = entwurf.art === k;
              return (
                <button data-tip={`${DOKUMENT_ART[k]} – Art des Dokuments`} className="immo-tippziel" key={k} type="button" role="radio" aria-checked={an} onClick={() => onEntwurf({ ...entwurf, art: k })}
                  style={{ height: 32, padding: '0 12px', borderRadius: 16, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
                    background: an ? 'var(--acc-fill)' : 'var(--bg)', color: an ? 'var(--acc-ink)' : 'var(--ink2)', boxShadow: an ? 'none' : 'inset 0 0 0 1px var(--line)' }}>
                  {DOKUMENT_ART[k]}
                </button>
              );
            })}
          </div>
          <div className="grid" style={{ gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <input value={entwurf.titel} onChange={(e) => onEntwurf({ ...entwurf, titel: e.target.value })} placeholder="Bezeichnung" aria-label="Bezeichnung" style={KLEINFELD} />
            <label className="flex items-center" style={{ ...KLEINFELD, gap: 6, padding: '0 10px' }}>
              <span className="whitespace-nowrap" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink3)' }}>gültig bis</span>
              <input type="date" value={entwurf.gueltigBis} onChange={(e) => onEntwurf({ ...entwurf, gueltigBis: e.target.value })}
                className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 13, color: 'var(--ink)' }} />
            </label>
          </div>
          <label data-tip="Datei wählen – PDF oder Bild" className="flex cursor-pointer items-center justify-center"
            style={{ minHeight: 64, padding: '8px 12px', borderRadius: 14, border: '1.5px dashed var(--line)', gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--ink2)', background: 'var(--bg)' }}>
            <UploadSimple size={16} weight="bold" color="var(--acc)" />
            <span className="min-w-0 truncate">{entwurf.datei ? entwurf.datei.name : 'Datei wählen'}</span>
            <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => onEntwurf({ ...entwurf, datei: e.target.files?.[0] || null })} />
          </label>
          <div className="flex justify-end" style={{ gap: 6 }}>
            <button data-tip="Abbrechen – Formular schließen" className="immo-tippziel" type="button" onClick={onSchliessen}
              style={{ height: 40, padding: '0 14px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink)' }}>Abbrechen</button>
            <button data-tip="Ablegen – Dokument in der Akte speichern" className="immo-tippziel" type="button" onClick={onAblegen} disabled={arbeitet}
              style={{ height: 40, padding: '0 16px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 800,
                background: 'var(--acc-fill)', color: 'var(--acc-ink)', opacity: arbeitet ? 0.5 : 1 }}>{arbeitet ? 'Lädt hoch …' : 'Ablegen'}</button>
          </div>
        </div>
      )}
      {dokumente.length === 0 ? (
        <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine Dokumente abgelegt</span>
      ) : dokumente.map((d) => {
        const bis = d.gueltig_bis ? ampelFarbe(ampel(d.gueltig_bis)) : null;
        return (
          <div key={d.id} className="flex flex-wrap items-center" style={{ gap: 12, padding: '10px 12px', borderRadius: 16, background: 'var(--fill)' }}>
            <span className="flex shrink-0 items-end justify-center"
              style={{ width: 34, height: 42, borderRadius: 8, paddingBottom: 5, background: 'var(--fill3)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
              <span style={{ fontSize: 7.5, fontWeight: 800, color: 'var(--bad)' }}>{d.datei_path ? endung(d.datei_name) : '—'}</span>
            </span>
            <div className="flex min-w-0 flex-col" style={{ flex: '1 1 160px', gap: 2 }}>
              <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{d.titel}</span>
              <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
                <span className="inline-flex items-center" style={{ height: 20, padding: '0 8px', borderRadius: 10, fontSize: 10.5, fontWeight: 800, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
                  {DOKUMENT_ART[d.art]}
                </span>
                {bis && (
                  <span data-tip="Gültig bis – Ablaufdatum des Dokuments" className="inline-flex items-center"
                    style={{ height: 20, padding: '0 8px', borderRadius: 10, fontSize: 10.5, fontWeight: 800, background: bis.flaeche, color: bis.farbe }}>
                    bis {datum(d.gueltig_bis)}
                  </span>
                )}
                <span style={{ fontSize: 11.5, color: 'var(--ink3)' }}>{datum(d.created_at.slice(0, 10))}</span>
              </div>
            </div>
            {d.datei_path && <RundKnopf icon={FileArrowDown} label="Öffnen" tip="Öffnen – Dokument in neuem Tab" onClick={() => onOeffnen(d)} />}
            {verwalten && <RundKnopf icon={Trash} label="Löschen" tip="Löschen – Dokument entfernen" gefahr onClick={() => onLoeschen(d)} />}
          </div>
        );
      })}
    </div>
  );
}
